import { Link } from 'react-router-dom';
import { makeStyles } from 'tss-react/mui';
import { getColors, typography } from '../tokens';
import { Fragment, type ReactNode } from 'react';

export interface BreadcrumbSegment {
  label: string;
  /** When provided the segment renders as a link; omit for the current (last) segment. */
  to?: string;
  /** Optional icon rendered before the first segment text. */
  icon?: ReactNode;
}

interface PageBreadcrumbsProps {
  segments: BreadcrumbSegment[];
}

const useStyles = makeStyles()(theme => {
  const c = getColors(theme.palette.mode);
  const isLight = theme.palette.mode === 'light';
  const fgAlpha = (a: number) =>
    isLight ? `rgba(0, 0, 0, ${a})` : `rgba(255, 255, 255, ${a})`;
  return {
    nav: {
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      marginBottom: 16,
      fontFamily: typography.fontFamilyCorpoSText,
      fontSize: 13,
      color: fgAlpha(0.5),
    },
    link: {
      color: fgAlpha(0.5),
      textDecoration: 'none',
      '&:hover': {
        color: c.primary.main,
        textDecoration: 'none',
      },
    },
    sep: {
      color: fgAlpha(0.3),
      margin: '0 2px',
    },
    current: {
      color: fgAlpha(0.8),
    },
  };
});

export const PageBreadcrumbs = ({
  segments,
}: Readonly<PageBreadcrumbsProps>) => {
  const { classes } = useStyles();
  return (
    <nav className={classes.nav}>
      {segments.map((seg, index) => (
        <Fragment key={`${index}-${seg.label}`}>
          {index > 0 && <span className={classes.sep}>/</span>}
          {seg.to ? (
            <Link to={seg.to} className={classes.link}>
              {index === 0 && seg.icon}
              {seg.label}
            </Link>
          ) : (
            <span className={classes.current}>
              {index === 0 && seg.icon}
              {seg.label}
            </span>
          )}
        </Fragment>
      ))}
    </nav>
  );
};
